import React, { useState, useEffect, forwardRef,useRef, useImperativeHandle } from "react";
import { Table, Spin, Tooltip, Popconfirm } from "antd";
import LocalExportButton from "./export/LocalExportButton";
import LocalExportHandler from "./export/LocalExportHandler";
import ExportButton from "./export/ExportButton";
import ExportHandler from "./export/ExportHandler";
import columns from "../utils/SearchConfig";
import searchPageLabels from "../search/searchPageLabels.json";
import { useSelector } from "react-redux";
import { exportAllEmployees } from "../../services/Service";

const EmployeeGridView = forwardRef(
  (
    {
      searchResults,
      page,
      pageSize,
      totalRecords,
      onPageChange,
      onPageSizeChange,
      searchPerformed,
      resetSelectedRows,
    },
    ref
  ) => {
    const { tooltip } = searchPageLabels;
    const [selectedRowKeys, setSelectedRowKeys] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const tableRef = useRef(null);
    const [isExportDisabled, setIsExportDisabled] = useState(true);
    const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 10,
      total: searchResults.length,
    });
    const token = useSelector((state) => state.auth.token);
    //Handles Loading indicator Logic
    useEffect(() => {
      if (searchResults.length === 0) {
        setLoading(false);
      } else {
        if (searchResults.length > 10) {
          setLoading(true);
          setTimeout(() => setLoading(false), 1000);
        } else {
          setLoading(false);
        }
      }
    }, [searchResults]);

    //Handles Local Export Button Enable/Disable
    useEffect(() => {
      setIsExportDisabled(selectedRowKeys.length === 0);
    }, [selectedRowKeys]);

    //Handle Local Export Function
    const handleLocalExport = () => {
      setIsExportDisabled(true); // Disable Local Export button after click
      LocalExportHandler({
        searchResults,
        selectedRowKeys,
      })();
      setSelectedRowKeys([]); // Unselect all rows after Local Export
    };

    //Handle Export Function
    const handleExport = async () => {
      if (!searchResults || searchResults.length === 0) {
        console.error("No search results to export.");
        return;
      }

      const exportAll = selectedRowKeys.size === 0;
      const isSearchFiltered = exportAll && searchPerformed;

      if (isSearchFiltered) {
        // Export search results directly
        ExportHandler({ searchResults: searchResults, selectedRowKeys: [] })();
        setSelectedRowKeys(new Set());
        return;
      }

      try {
        const allRecords = await exportAllEmployees(token);

        let rowsToExport;

        if (exportAll) {
          // Export all records
          rowsToExport = allRecords;
        } else {
          // Export selected records
          rowsToExport = allRecords.filter((row) =>
            selectedRowKeys.has(row.uid)
          );
        }

        ExportHandler({
          searchResults: rowsToExport,
          selectedRowKeys: exportAll ? [] : Array.from(selectedRowKeys),
        })();
        setSelectedRowKeys(new Set());
      } catch (error) {
        console.error(
          `Error fetching records for ${
            exportAll ? "all" : "selected"
          } export:`,
          error
        );
      }
    };

    const exportTooltip =
      searchResults.length === 0 && searchPerformed
        ? searchPageLabels.tooltip.noDataToExport
        : selectedRowKeys.size === 0
        ? tooltip.exportAllSuccess.replace("{count}", totalRecords)
        : tooltip.exportSelectedSuccess.replace(
            "{count}",
            selectedRowKeys.size
          );

    useImperativeHandle(ref, () => ({
      resetSelectedRows: () => {
        setSelectedRowKeys(new Set());
      },
      focus: () => {
        if (tableRef.current) {
          tableRef.current.focus();
        }
      },
    }));

    useEffect(() => {
      setSelectedRowKeys(new Set());
    }, [resetSelectedRows]);

    const rowSelection = {
      selectedRowKeys: Array.from(selectedRowKeys),
      onChange: (keys) => {
        setSelectedRowKeys(new Set(keys));
      },
      preserveSelectedRowKeys: true, // Ensures selected rows persist across pages
    };

    //Dynamic Page Size Options for pagination
    const getPageSizeOptions = () => {
      const defaultOptions = ["10", "20", "50", "100"];
      const validOptions = defaultOptions.filter(
        (size) => Number(size) <= Math.max(totalRecords, 10)
      );
      if (!validOptions.includes(totalRecords.toString())) {
        validOptions.push(totalRecords.toString());
      }
      return validOptions;
    };

    return (
      <div
        ref={tableRef}
        tabIndex={-1}
        className="bg-white text-white flex flex-col rounded-md shadow-lg max-w-[1050px] mt-4"
      >
        <div className="flex justify-end items-center h-12 p-6">
          {searchResults.length > 0 ? (
            <Popconfirm
              title="Are you sure you want to export?"
              onConfirm={() => handleExport()}
              onCancel={() => setSelectedRowKeys(new Set())}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title={exportTooltip} placement="left">
                <span>
                  <ExportButton onExport={() => {}} />
                </span>
              </Tooltip>
            </Popconfirm>
          ) : (
            <Tooltip
              title={searchPageLabels.tooltip.noDataToExport}
              placement="left"
            >
              <span>
                <ExportButton onExport={() => {}} />
              </span>
            </Tooltip>
          )}
        </div>
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <Spin size="large" />
          </div>
        ) : searchResults.length === 0 && searchPerformed ? (
          <div className="pl-4 pr-4 pb-4">
            <Table
              columns={columns}
              rowKey={(record) => `${record.uid}`}
              dataSource={searchResults} // Render the table with empty data
              pagination={{
                current: page,
                pageSize: pageSize,
                total: totalRecords,
                showSizeChanger: true,
                pageSizeOptions: getPageSizeOptions(),
                position: ["bottomCenter"],
                onChange: (newPage, newPageSize) => {
                  onPageChange(newPage);
                  onPageSizeChange(newPageSize);
                },
              }}
              scroll={
                searchResults.length > 0
                  ? { x: "max-content", y: 400 }
                  : undefined
              }
              sticky={searchResults.length > 0 ? { offsetHeader: 0 } : false}
              tableLayout="fixed"
              components={{
                header: {
                  wrapper: (props) => (
                    <thead
                      {...props}
                      className="text-white text-left border bg-[var(--color-primary)]"
                    >
                      {props.children}
                    </thead>
                  ),
                },
              }}
              rowSelection={rowSelection}
              bordered
              className="border-2 color-[#183D5D] shadow-lg rounded-md w-full"
            />
          </div>
        ) : (
          <div className="pl-4 pr-4 pb-4">
            <Table
              columns={columns}
              rowKey={(record) => `${record.uid}`}
              dataSource={searchResults}
              pagination={{
                current: page,
                pageSize: pageSize,
                total: totalRecords,
                showSizeChanger: true,
                pageSizeOptions: getPageSizeOptions(),
                position: ["bottomCenter"],
                onChange: (newPage, newPageSize) => {
                  onPageChange(newPage);
                  onPageSizeChange(newPageSize);
                },
              }}
              scroll={
                searchResults.length > 0
                  ? { x: "max-content", y: 400 }
                  : undefined
              }
              // Removes scroll when no data
              sticky={searchResults.length > 0 ? { offsetHeader: 0 } : false}
              tableLayout="fixed"
              components={{
                header: {
                  wrapper: (props) => (
                    <thead
                      {...props}
                      className="text-white text-left border bg-[var(--color-primary)]"
                    >
                      {props.children}
                    </thead>
                  ),
                },
              }}
              rowSelection={rowSelection}
              bordered
              className="border-2 color-[#183D5D] shadow-lg rounded-md w-full"
            />
          </div>
        )}
      </div>
    );
  }
);

export default EmployeeGridView;
